'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import posthog from '@/lib/analytics/posthog-lazy'
import { accountReadiness } from '@/lib/analytics/account-readiness'
import { recordAcquisitionTouch } from '@/lib/analytics/acquisition-touch'
import { createAcquisitionTracker } from '@/lib/analytics/acquisition'
import {
  cachedAccountConsent,
  getAnalyticsMode,
  onAnalyticsModeChange,
} from '@/lib/analytics/consent'

const tracker = createAcquisitionTracker(posthog, (properties) => {
  const cached = cachedAccountConsent()
  const ready = accountReadiness()
  if (!ready.resolved || (cached && ready.userId !== cached.userId)) return
  recordAcquisitionTouch(properties)
})

/** Trusted browser engagement only; server crawler fetches cannot emit this event. */
export function AcquisitionTracker() {
  const pathname = usePathname()
  useEffect(() => {
    const engage = (event: Event) =>
      tracker.engage({
        pathname: window.location.pathname,
        search: window.location.search,
        referrer: document.referrer,
        mode: getAnalyticsMode(),
        trusted: event.isTrusted,
        visible: document.visibilityState === 'visible',
        webdriver: navigator.webdriver,
        userAgent: navigator.userAgent,
        release: process.env.NEXT_PUBLIC_WEB_BUILD_ID || 'development',
      })
    document.addEventListener('pointerdown', engage, { passive: true })
    document.addEventListener('keydown', engage)
    const unsubscribe = onAnalyticsModeChange(() => tracker.consentChanged())
    return () => {
      document.removeEventListener('pointerdown', engage)
      document.removeEventListener('keydown', engage)
      unsubscribe()
    }
  }, [pathname])
  return null
}
